import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import "./CustomerList.css";

const SupplierOrderHistory = () => {
  const [supplier, setSupplier] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [filteredPurchases, setFilteredPurchases] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const { id } = useParams();
  const navigate = useNavigate();

  // Sample supplier data
  const sampleSuppliers = [
    {
      id: 1,
      name: "SSS Enterprises",
      mobile: "9876543210",
      gst_number: "33AALF51701P1Z1",
      state: "Tamil Nadu",
      city: "Sivakasi",
      pin_code: "626189",
      address: "264/SA THAMMANACKENPATTI SIVAKASI-626189",
      totalPurchases: 8,
      totalAmount: 187650.75,
      lastPurchaseDate: "2025-11-21"
    }
  ];

  // Sample purchases data matching your ViewPurchase structure
  const samplePurchases = [
    {
      id: 1,
      purchaseId: "PUR-001",
      date: "2025-11-21",
      supplier: "SSS Enterprises",
      supplierPhone: "9876543210",
      supplierAddress: "123 Main Street, Bangalore, Karnataka - 560001",
      status: "Received",
      items: [
        {
          id: 1,
          productCode: "PB004",
          item: "Pin Box - (00:4)",
          purchaseRate: 180.00,
          qty: 73,
          total: 13140.00
        }
      ],
      subTotal: 13140.00,
      gstPercentage: 18,
      gstAmount: 2365.20,
      grandTotal: 15505.20
    },
    {
      id: 2,
      purchaseId: "PUR-002",
      date: "2025-11-18",
      supplier: "SSS Enterprises",
      supplierPhone: "9876543210",
      supplierAddress: "123 Main Street, Bangalore, Karnataka - 560001",
      status: "Pending",
      items: [
        {
          id: 1,
          productCode: "GT003",
          item: "Gum Tape - 3 Inches",
          purchaseRate: 150.00,
          qty: 50,
          total: 7500.00
        },
        {
          id: 2,
          productCode: "SB005",
          item: "Sparkler Box",
          purchaseRate: 135.00,
          qty: 10,
          total: 1350.00
        }
      ],
      subTotal: 8850.00,
      gstPercentage: 18,
      gstAmount: 1593.00,
      grandTotal: 10443.00
    },
    {
      id: 3,
      purchaseId: "PUR-003",
      date: "2025-11-15",
      supplier: "SSS Enterprises",
      supplierPhone: "9876543210",
      supplierAddress: "123 Main Street, Bangalore, Karnataka - 560001",
      status: "Received",
      items: [
        {
          id: 1,
          productCode: "CB012",
          item: "Cardboard Box - Large",
          purchaseRate: 220.00,
          qty: 25,
          total: 5500.00
        }
      ],
      subTotal: 5500.00,
      gstPercentage: 18,
      gstAmount: 990.00,
      grandTotal: 6490.00
    }
  ];

  useEffect(() => {
    const foundSupplier = sampleSuppliers.find(s => s.id === parseInt(id));
    setSupplier(foundSupplier);

    if (foundSupplier) {
      const supplierPurchases = samplePurchases.filter(purchase => 
        purchase.supplier === foundSupplier.name
      );
      setPurchases(supplierPurchases);
      setFilteredPurchases(supplierPurchases);
    }
  }, [id]);

  useEffect(() => {
    let filtered = purchases;

    if (searchTerm) {
      filtered = filtered.filter(purchase =>
        purchase.purchaseId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        purchase.items.some(item => 
          item.item.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(purchase => purchase.status === statusFilter);
    }

    if (dateFilter) {
      filtered = filtered.filter(purchase => purchase.date === dateFilter);
    }

    setFilteredPurchases(filtered);
  }, [searchTerm, statusFilter, dateFilter, purchases]);

  const getStatusVariant = (status) => {
    switch(status) {
      case 'Received': return 'status-approved';
      case 'Pending': return 'status-pending';
      case 'Cancelled': return 'status-rejected';
      case 'Draft': return 'status-default';
      default: return 'status-default';
    }
  };

  const handlePrintPurchase = (purchaseId) => {
    window.open(`/sds_polytechnic/purchaseprint/${purchaseId}`, '_blank');
  };

  const calculateTotalItems = (items) => {
    return items.reduce((total, item) => total + item.qty, 0);
  };

  const calculateTotalAmount = () => {
    return filteredPurchases.reduce((total, purchase) => total + purchase.grandTotal, 0);
  };

  const {
    currentPage,
    totalPages,
    currentItems: currentPurchases,
    handlePageChange,
    indexOfFirstItem
  } = usePagination(filteredPurchases, 10);

  if (!supplier) {
    return (
      <div className="outer-container">
        <div className="text-center py-5">
          <p>Supplier not found.</p>
          <button className="btn-top" onClick={() => navigate(-1)}>
            ← Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="outer-container">
      {/* Header Section */}
      <div className="customer-header">
        <div>
          <h3>Purchase History</h3>
          <p style={{color: '#6c757d', margin: 0}}>Supplier: {supplier.name}</p>
        </div>
        <button className="btn-top" onClick={() => navigate(-1)}>
          ← Back to Suppliers
        </button>
      </div>

      {/* Supplier Summary Cards */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px'}}>
        <div className="stat-card" style={{backgroundColor: '#294664', color: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center'}}>
          <h4 style={{margin: '0 0 5px 0', fontSize: '1.5rem'}}>{purchases.length}</h4>
          <small>Total Purchases</small>
        </div>
        <div className="stat-card" style={{backgroundColor: '#28a745', color: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center'}}>
          <h4 style={{margin: '0 0 5px 0', fontSize: '1.5rem'}}>₹{calculateTotalAmount().toLocaleString('en-IN')}</h4>
          <small>Total Amount</small>
        </div>
        <div className="stat-card" style={{backgroundColor: '#17a2b8', color: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center'}}>
          <h4 style={{margin: '0 0 5px 0', fontSize: '1.5rem'}}>{supplier.totalPurchases}</h4>
          <small>All Time Purchases</small>
        </div>
        <div className="stat-card" style={{backgroundColor: '#ffc107', color: 'black', padding: '15px', borderRadius: '8px', textAlign: 'center'}}>
          <h4 style={{margin: '0 0 5px 0', fontSize: '1.5rem'}}>{new Date(supplier.lastPurchaseDate).toLocaleDateString('en-IN')}</h4>
          <small>Last Purchase</small>
        </div>
      </div>

      {/* Supplier Details */}
      <div style={{backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', marginBottom: '20px', padding: '20px'}}>
        <h5 style={{marginBottom: '15px', color: '#294664'}}>Supplier Information</h5>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px'}}>
          <div>
            <p style={{margin: '5px 0'}}><strong>Name:</strong> {supplier.name}</p>
            <p style={{margin: '5px 0'}}><strong>Mobile:</strong> {supplier.mobile}</p>
            <p style={{margin: '5px 0'}}><strong>GSTIN:</strong> {supplier.gst_number}</p>
          </div>
          <div>
            <p style={{margin: '5px 0'}}><strong>Address:</strong> {supplier.address}</p>
            <p style={{margin: '5px 0'}}><strong>City:</strong> {supplier.city}, {supplier.state} - {supplier.pin_code}</p>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <h5 style={{marginBottom: '15px', color: '#294664'}}>Filter Purchases</h5>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px'}}>
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: '500'}}>Search Purchases</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by purchase ID or product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px'}}
            />
          </div>
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: '500'}}>Status</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px'}}
            >
              <option value="all">All Status</option>
              <option value="Received">Received</option>
              <option value="Pending">Pending</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Draft">Draft</option>
            </select>
          </div>
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: '500'}}>Purchase Date</label>
            <input
              type="date"
              className="form-control"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px'}}
            />
          </div>
          <div>
            <label style={{display: 'block', marginBottom: '5px', fontWeight: '500'}}>&nbsp;</label>
            <button
              className="btn-top"
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
                setDateFilter("");
              }}
              style={{width: '100%'}}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Purchases Table */}
      <div className="table-pagination-wrapper">
        <div className="table-pagination-content">
          <table className="customer-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Purchase ID</th>
                <th>Purchase Date</th>
                <th>Status</th>
                <th>Items</th>
                <th>Total Items</th>
                <th className="text-end">Amount (₹)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentPurchases.map((purchase, index) => (
                <tr key={purchase.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>
                    <strong>{purchase.purchaseId}</strong>
                  </td>
                  <td>{purchase.date}</td>
                  <td>
                    <span className={`status-badge ${getStatusVariant(purchase.status)}`}>
                      {purchase.status}
                    </span>
                  </td>
                  <td>
                    <div style={{fontSize: '0.875rem'}}>
                      {purchase.items.slice(0, 2).map((item, i) => (
                        <div key={i}>
                          {item.item} ({item.qty} pcs)
                        </div>
                      ))}
                      {purchase.items.length > 2 && (
                        <div style={{color: '#6c757d'}}>
                          +{purchase.items.length - 2} more items
                        </div>
                      )}
                    </div>
                  </td>
                  <td>{calculateTotalItems(purchase.items)}</td>
                  <td className="text-end" style={{fontWeight: 'bold'}}>
                    ₹{purchase.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td>
                    <div className="action-icons">
                      <Link
                        to={`/viewpurchase/${purchase.id}`}
                        className="view-icon"
                        title="View Purchase"
                      >
                        👁️
                      </Link>
                      <button
                        className="edit-icon"
                        onClick={() => handlePrintPurchase(purchase.id)}
                        title="Print Purchase"
                      >
                        🖨️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {currentPurchases.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center" style={{padding: '40px'}}>
                    <div style={{color: '#6c757d'}}>
                      <div style={{fontSize: '48px', marginBottom: '10px'}}>📦</div>
                      <p>No purchases found for this supplier</p>
                      <small>Try adjusting your filters or search terms</small>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {/* Summary Footer */}
      {filteredPurchases.length > 0 && (
        <div className="summary-footer">
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px'}}>
            <div>
              <strong>Total Purchases:</strong> {filteredPurchases.length}
            </div>
            <div>
              <strong>Total Items:</strong> {filteredPurchases.reduce((total, purchase) => total + calculateTotalItems(purchase.items), 0)}
            </div>
            <div>
              <strong>Total Amount:</strong> ₹{calculateTotalAmount().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierOrderHistory;